<template>
  <Dialog
    :open="open"
    title="Delete Organization"
    size="md"
    @update:open="$emit('update:open', $event)"
  >
    <div class="sm:flex sm:items-start">
      <div
        class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900 sm:mx-0 sm:h-10 sm:w-10"
      >
        <ExclamationTriangleIcon
          class="h-6 w-6 text-red-600 dark:text-red-400"
          aria-hidden="true"
        />
      </div>
      <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
        <div class="mt-2">
          <p class="text-sm text-gray-500 dark:text-gray-400">
            Are you sure you want to delete <strong>{{ organization?.name }}</strong
            >? This action cannot be undone. All data, members, and resources associated with this
            organization will be permanently deleted.
          </p>
        </div>
      </div>
    </div>

    <div class="mt-5 space-y-4">
      <div>
        <label
          for="confirm-name"
          class="block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Type the organization name to confirm:
        </label>
        <input
          type="text"
          id="confirm-name"
          v-model="confirmName"
          class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm"
          :placeholder="organization?.name"
        />
      </div>
    </div>

    <template #footer>
      <div class="sm:flex sm:flex-row-reverse gap-3">
        <Button
          variant="danger"
          :disabled="!isConfirmed"
          :loading="isLoading"
          full-width
          @click="handleDelete"
        >
          Delete Organization
        </Button>
        <Button variant="outline" full-width class="mt-3 sm:mt-0" @click="$emit('update:open', false)">
          Cancel
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
  import { Dialog, Button } from '@asabdev/asab-ui';
  import type { Organization } from '../types';

  const props = defineProps<{
    open: boolean;
    organization: Organization | null;
    isLoading?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'update:open', value: boolean): void;
    (e: 'confirm'): void;
  }>();

  const confirmName = ref('');

  const isConfirmed = computed(() => {
    return confirmName.value === props.organization?.name;
  });

  const handleDelete = () => {
    if (isConfirmed.value) {
      emit('confirm');
      confirmName.value = '';
    }
  };
</script>
